import { useState, type FC, type FormEvent } from "react";
import { addDeviceGroupWithKeys } from "../../stores/device_group_store";
import { listAccountData } from "../../stores/account_store";
import { KeyModeSelector } from "./key_mode_selector";

export const CreateGroupModal: FC<{
  policies: { id: string; name: string }[];
  onClose: () => void;
}> = ({ policies, onClose }) => {
  const [name, setName] = useState("");
  const [certificatePolicyId, setCertificatePolicyId] = useState(
    policies[0]?.id ?? ""
  );
  const [keyMode, setKeyMode] = useState<
    "customer-managed" | "verimatrix-managed"
  >("verimatrix-managed");
  const [certificate, setCertificate] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRefreshingPolicies, setIsRefreshingPolicies] = useState(false);

  const handleRefreshPolicies = async () => {
    setError(undefined);
    setIsRefreshingPolicies(true);
    try {
      await listAccountData();
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : "Failed to refresh certificate policies.";
      setError(msg);
    } finally {
      setIsRefreshingPolicies(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(undefined);
    const trimmedName = name.trim();
    if (trimmedName === "" || certificatePolicyId === "") return;
    if (
      keyMode === "customer-managed" &&
      (certificate.trim() === "" || privateKey.trim() === "")
    ) {
      setError(
        "Certificate and private key are required for customer-managed keys."
      );
      return;
    }
    setIsSubmitting(true);
    try {
      await addDeviceGroupWithKeys({
        name: trimmedName,
        certificatePolicyId,
        keyMode,
        ...(keyMode === "customer-managed" && { certificate, privateKey }),
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to create device group."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vmx-modal">
      <div className="vmx-modal-header">
        <div className="vmx-modal-title">New Device Group</div>
        <button
          className="vmx-btn vmx-btn-ghost vmx-btn-icon"
          onClick={onClose}
        >
          ✕
        </button>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="vmx-modal-body">
          <div className="vmx-form-group">
            <label className="vmx-label">Group Name</label>
            <input
              className="vmx-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Smart TV Fleet"
              required
              autoFocus
            />
          </div>
          <div className="vmx-form-group">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <label className="vmx-label">Certificate Policy</label>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                  onClick={handleRefreshPolicies}
                  title="Refresh certificate policies"
                  disabled={isRefreshingPolicies}
                >
                  {isRefreshingPolicies ? "⟳ Refreshing..." : "⟳ Refresh"}
                </button>
                <a
                  href="/certificate-policies?action=create"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="vmx-btn vmx-btn-ghost vmx-btn-sm"
                  style={{ textDecoration: "none" }}
                >
                  ＋ New Certificate Policy
                </a>
              </div>
            </div>
            {policies.length === 0 ? (
              <div
                style={{
                  padding: "16px",
                  background: "var(--vmx-gray-50)",
                  borderRadius: "var(--vmx-radius)",
                  textAlign: "center",
                  color: "var(--vmx-gray-500)",
                  fontSize: "0.8125rem",
                }}
              >
                No certificate policies available. Create one first.
              </div>
            ) : (
              <select
                className="vmx-select"
                value={certificatePolicyId}
                onChange={(e) => setCertificatePolicyId(e.target.value)}
                required
              >
                {policies.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <KeyModeSelector
            keyMode={keyMode}
            onKeyModeChange={setKeyMode}
            certificate={certificate}
            onCertificateChange={setCertificate}
            privateKey={privateKey}
            onPrivateKeyChange={setPrivateKey}
          />
          {error !== undefined && (
            <div
              style={{
                marginTop: 4,
                fontSize: "0.8125rem",
                color: "var(--vmx-danger)",
              }}
            >
              {error}
            </div>
          )}
        </div>
        <div className="vmx-modal-footer">
          <button
            type="button"
            className="vmx-btn vmx-btn-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="vmx-btn vmx-btn-primary"
            disabled={
              policies.length === 0 ||
              isSubmitting ||
              (keyMode === "customer-managed" &&
                (certificate.trim() === "" || privateKey.trim() === ""))
            }
          >
            {isSubmitting ? "Creating..." : "Create Group"}
          </button>
        </div>
      </form>
    </div>
  );
};
